import { isAppointment } from "@/features/appointments/utils";
import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import {
  type AppointmentFilterActions,
  type CalenderAppointmentsSearchFilters,
  isTeamSelected,
} from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import {
  selectEmail,
  selectTeam,
} from "@/features/calendar/calendarFilter/appointmentFilterSelector";
import {
  UNASSIGNED_CALENDAR_EVENT,
  UNASSIGNED_OPEN_SLOT_CALENDAR_EVENT,
} from "@/features/calendar/constants";
import { useIsScheduleEnabled } from "@/features/calendar/hooks/useIsScheduleEnabled";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import { filterTeamCoworkers } from "@/features/calendar/team-calendar/utils";
import { getCalStartHour, mapToCalendarItem } from "@/features/calendar/utils";
import { filterVisibleTasks } from "@/features/calendar/utils/filterVisibleTasks";
import { DndContext } from "@dnd-kit/core";
import styled from "@emotion/styled";
import {
  type Appointment,
  type Schedule,
  type Task,
  useMyTeams,
  useTeam,
} from "@repo/api";
import {
  DualButton,
  Hyperlink,
  InlineMessage,
  zoomInIcon,
  zoomOutIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  FlexDiv,
  SectionContainer,
  WidthAwareContainer,
  useSettings,
} from "@repo/ui";
import { areIntervalsOverlapping, endOfDay, startOfDay } from "date-fns";
import isEmpty from "lodash/isEmpty";
import { useRef } from "react";

import { useHandleDrag } from "../hooks/useHandleDrag";
import { getZoomHourWidth } from "../utils/utils";
import { CategoryRow } from "./CategoryRow";
import { CoWorkers } from "./CoWorkers";
import { CurrentTimeLine, zoomTimeline } from "./CurrentTimeLine";
import { DragPreviewProvider } from "./DragPreviewContext";
import { UnassignedAppointments } from "./UnassignedAppointments";
import { HourTitles } from "./components";
import { NAME_COL_WIDTH } from "./constants";
import { groupEvents } from "./groupEvents";

const CalendarScrollContainer = styled.div`
  overflow: auto;
  max-height: min(70dvh, 900px);
`;

export const dedupeSchedules = (schedules: Schedule[] | undefined) => {
  return (
    schedules?.reduce((acc, curr) => {
      const isExactDuplicate = acc.some(
        (item) =>
          item.coworkerEmail === curr.coworkerEmail &&
          item.scheduleDate === curr.scheduleDate &&
          item.shiftStartTime === curr.shiftStartTime &&
          item.shiftEndTime === curr.shiftEndTime &&
          item.shiftType === curr.shiftType &&
          item.activity === curr.activity &&
          (item.shiftId ?? "") === (curr.shiftId ?? "")
      );

      if (!isExactDuplicate) {
        acc.push(curr);
      }

      return acc;
    }, [] as Schedule[]) ?? []
  );
};

// 48 = 12h * BLOCKS_PER_HOUR (15 min slots)
// 50px = space for BLOCKS_PER_HOUR
const CalendarContainer = styled.div<{
  calendarHours: number;
  hourWidth: number;
}>`
  position: relative;
  width: ${({ calendarHours, hourWidth }) =>
    calendarHours * hourWidth + NAME_COL_WIDTH}px;
`;
export const TeamCalendar = ({
  actions,
  items,
  openSlotItems,
  schedules,
  allSchedules,
  openModal,
  filters,
  mutateSearchAppointments,
}: {
  actions: AppointmentFilterActions;
  items: (Appointment | Task)[];
  openSlotItems: (Appointment | Task)[];
  schedules: Schedule[] | undefined;
  allSchedules?: Schedule[] | undefined;
  filters: CalenderAppointmentsSearchFilters;
  openModal: (props: OpenModalProps) => void;
  mutateSearchAppointments?: () => void;
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const { saveTask } = useCalendarContext();
  const { calendar } = useSettings();
  const selectedTeamId = selectTeam(filters);
  const selectedEmailIds = selectEmail(filters);
  const teamCalendar = filters.teamCalendar;
  const { data: team } = useTeam(selectedTeamId);
  const { data: myTeams } = useMyTeams();

  const dedupedSchedules = dedupeSchedules(schedules);

  const isScheduleEnabled =
    useIsScheduleEnabled(filters.selectedDate, selectedTeamId) &&
    dedupedSchedules !== undefined;
  const mappedItems = mapToCalendarItem(filterVisibleTasks(items)).filter(
    (item) =>
      areIntervalsOverlapping(
        { start: item.start, end: item.end },
        {
          start: startOfDay(filters.selectedDate),
          end: endOfDay(filters.selectedDate),
        }
      )
  );

  const { calStartHour, totalHours } = getCalStartHour(
    mappedItems,
    filters.selectedDate
  );
  const { coWorkers, unassigned } = groupEvents({
    items: mappedItems,
    schedules: dedupedSchedules,
    workHoursSchedules: allSchedules
      ? dedupeSchedules(allSchedules)
      : dedupedSchedules,
    teams: team ? [team] : [],
    startHour: calStartHour,
    endHour: calStartHour + totalHours,
    selectedDate: filters.selectedDate,
    selectedEmailId: isTeamSelected(teamCalendar)
      ? (team?.teamMembers.map((m) => m.emailId) ?? [])
      : selectedEmailIds,
    selectedTeam: selectedTeamId,
    enableWorkhours: isScheduleEnabled,
  });

  const unassignedOpenSlotItems = openSlotItems.filter(
    (item) =>
      isAppointment(item) && !item.assignedCoworker && !item.assignedTeam
  );
  const mappedOpenSlotItems = mapToCalendarItem(
    filterVisibleTasks(unassignedOpenSlotItems)
  )
    .filter((item) =>
      areIntervalsOverlapping(
        { start: item.start, end: item.end },
        {
          start: startOfDay(filters.selectedDate),
          end: endOfDay(filters.selectedDate),
        }
      )
    )
    .map((item) => ({ ...item, emailId: UNASSIGNED_CALENDAR_EVENT }));
  const { unassigned: openSlotUnassigned } = groupEvents({
    items: mappedOpenSlotItems,
    schedules: [],
    teams: [],
    startHour: calStartHour,
    endHour: calStartHour + totalHours,
    selectedDate: filters.selectedDate,
    enableWorkhours: false,
  });

  const unassignedOpenSlots = openSlotUnassigned.map((lane) => ({
    ...lane,
    emailId: UNASSIGNED_OPEN_SLOT_CALENDAR_EVENT,
    meetings: lane.meetings.map((row) =>
      row.map((event) => ({
        ...event,
        emailId: UNASSIGNED_OPEN_SLOT_CALENDAR_EVENT,
      }))
    ),
    offHourMeetings: lane.offHourMeetings.map((event) => ({
      ...event,
      emailId: UNASSIGNED_OPEN_SLOT_CALENDAR_EVENT,
    })),
  }));

  const selectedTeamName = team
    ? `${team.teamName} (${t("label.ID")}: ${team.teamId})`
    : undefined;

  const { availableCw, unAvailableCw } = filterTeamCoworkers({
    coworkers: coWorkers,
    teamCalendarFilter: teamCalendar,
    calendarSettings: calendar,
  });

  const {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    snapToGridModifier,
    sensors,
    dragPreview,
  } = useHandleDrag({
    onUpdateTask: saveTask,
    mutateSearchAppointments,
    totalHours,
    zoom: filters.calendarSettings,
  });

  return (
    <SectionContainer>
      {isEmpty(myTeams) && (
        <InlineMessage
          title={t("prompt.not_in_team")}
          body={t("prompt.not_in_team_desc")}
          variant="informative"
        />
      )}
      <CalendarScrollContainer>
        <WidthAwareContainer
          setWidth={(width) => {
            actions.setZoomWidth(width);
          }}
        >
          <CalendarContainer
            calendarHours={totalHours}
            hourWidth={getZoomHourWidth(filters.calendarSettings, totalHours)}
          >
            <DndContext
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              sensors={sensors}
              modifiers={[snapToGridModifier]}
            >
              <DragPreviewProvider value={{ dragPreview }}>
                <SectionContainer>
                  <HourTitles
                    totalHours={totalHours}
                    startHour={calStartHour}
                    zoom={filters.calendarSettings}
                  />
                  {unassigned && unassigned.length > 0 && (
                    <UnassignedAppointments
                      totalHours={totalHours}
                      startHour={calStartHour}
                      unassigned={unassigned}
                      openModal={openModal}
                      filters={filters}
                    />
                  )}
                  {unassignedOpenSlots && unassignedOpenSlots.length > 0 && (
                    <UnassignedAppointments
                      totalHours={totalHours}
                      startHour={calStartHour}
                      unassigned={unassignedOpenSlots}
                      openModal={openModal}
                      filters={filters}
                      title={t("label.unassigned_open_slots_in_team_unit")}
                    />
                  )}
                  <CategoryRow
                    title={selectedTeamName ?? t("prompt.no_team_selected")}
                  >
                    <CoWorkers
                      totalHours={totalHours}
                      startHour={calStartHour}
                      coWorkers={availableCw}
                      openModal={openModal}
                      filters={filters}
                    />
                  </CategoryRow>
                  {isScheduleEnabled && unAvailableCw.length > 0 && (
                    <CategoryRow title={t("label.not_available")}>
                      <CoWorkers
                        totalHours={totalHours}
                        startHour={calStartHour}
                        coWorkers={unAvailableCw}
                        openModal={openModal}
                        filters={filters}
                      />
                    </CategoryRow>
                  )}
                </SectionContainer>
              </DragPreviewProvider>
            </DndContext>
            <CurrentTimeLine
              totalHours={totalHours}
              startHour={calStartHour}
              selectedDate={filters.selectedDate}
              zoom={filters.calendarSettings}
              timelineRef={timelineRef}
            />
          </CalendarContainer>
        </WidthAwareContainer>
      </CalendarScrollContainer>
      <FlexDiv alignItems="center" justifyContent="space-between">
        <Hyperlink
          text={t("action.see_enabled_services")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actions.showAvailableServices(true);
          }}
        />
        <DualButton
          type="primary"
          small
          orientation="horizontal"
          ssrIconFirst={zoomInIcon}
          ssrIconSecond={zoomOutIcon}
          onClickFirst={() => {
            actions.setZoom("NORMAL", filters.calendarSettings.zoomWidth);
            zoomTimeline(timelineRef, 100);
          }}
          onClickSecond={() => {
            actions.setZoom("ZOOM_OUT", filters.calendarSettings.zoomWidth);
          }}
        />
      </FlexDiv>
    </SectionContainer>
  );
};
